import { FloatingPosition } from '@mantine/core/lib/Floating';
import {
  DateTimePicker as MantineDateTimePicker,
  DateTimePickerProps as MantineDateTimePickerProps,
} from '@mantine/dates';
import panic from 'errors/panic';
import { useMemo } from 'react';

interface DateTimeInputProps extends Omit<MantineDateTimePickerProps, 'value' | 'onChange'> {
  value?: string;
  onChange: (value?: string) => void;
  popoverPosition?: FloatingPosition;
}

/**
 * Converts a parseable ISO datetime string to a Date value.
 */
function parseIsoDateTime(value?: string): Date | null {
  if (value === undefined || value === '') {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    panic(new Error(`Invalid datetime: ${value}`));
    return null;
  }

  return date;
}

/**
 * Converts the selected Date value to an ISO datetime string.
 */
function stringifyIsoDateTime(value: Date | null): string | undefined {
  if (!value) {
    return undefined;
  }

  if (Number.isNaN(value.getTime())) {
    return undefined;
  }

  return value.toISOString();
}

/**
 * String-based datetime input backed by Mantine DateTimePicker.
 */
export default function DateTimeInput({ value, onChange, popoverPosition = 'left', ...rest }: DateTimeInputProps) {
  const dateValue = useMemo(() => parseIsoDateTime(value), [value]);

  return (
    <MantineDateTimePicker
      value={dateValue}
      onChange={(nextValue) => onChange(stringifyIsoDateTime(nextValue))}
      valueFormat="DD.MM.YYYY HH:mm"
      popoverProps={{ position: popoverPosition, withinPortal: true, zIndex: 10000 }}
      {...rest}
    />
  );
}
